import { Immutable } from "immer";
import React from "react";

import type { FeaturesModule as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type Props = Immutable<{
    value: Value;
}>;

type WagtailImage = NonNullable<Value["features_image"]>;

/**
 * Reproduces the legacy ``{% image x original %}`` output:
 * ``<img alt="{default_alt_text}" src="{url}" width="{w}" height="{h}">``
 * using the serialized ``renditions["original"]`` data (falling back to the
 * base image url/dimensions if the rendition is somehow missing). Like the
 * Django tag, ``{% image None ... %}`` renders nothing, so a null/undefined
 * image returns null. ``className`` mirrors the optional ``class=".."``
 * attribute of the tag (omitted entirely when not passed).
 */
const WagtailOriginalImage = (
    props: Immutable<{
        image: WagtailImage | null | undefined;
        className?: string;
    }>,
): React.JSX.Element | null => {
    const image = props.image;
    if (!image) {
        return null;
    }
    const rendition = image.renditions["original"];
    return (
        <img
            alt={image.default_alt_text}
            className={props.className}
            src={rendition?.url ?? image.url}
            width={rendition?.width ?? image.width}
            height={rendition?.height ?? image.height}
            loading="lazy"
            decoding="async"
        />
    );
};

/**
 * React port of the sealy ``FeaturesModule`` block (legacy template
 * ``cms/components/features-module.html``). Markup parity notes:
 *
 * - The first ``<section>`` always renders. It carries the module's
 *   ``withImage`` class (plus ``left`` for left orientation) when a main
 *   image is set, and an empty ``class=""`` otherwise, as the legacy
 *   template did.
 * - ``features_title`` uses Django truthiness: an empty string renders no
 *   ``<h2>`` at all.
 * - ``features_description`` is a server-rendered rich-text string that
 *   already includes the ``<div class="rich-text">`` wrapper (it is the
 *   rendered RichTextBlock, exactly what legacy ``{{
 *   value.features_description }}`` emitted). Inject it verbatim; the
 *   ``.featuresBlock :global(.rich-text)`` SCSS rules are descendant
 *   selectors, so the intermediate (classless) host div is harmless. When
 *   blank, legacy printed an empty string, so render nothing.
 * - CTA buttons: ``href`` falls back to ``"#"`` when no page is linked
 *   (legacy ``{% if cta.link %}...{% else %}href="#"{% endif %}``).
 * - The features ``<ul>`` only renders for a non-empty list; a feature with
 *   a null icon still renders its ``<li>``/``<h6>``/``<p>`` (Django's
 *   ``{% image None original %}`` printed nothing).
 * - When ``features_image`` is set, the image ``<section>`` AND the overlay
 *   ``<img>`` both render as direct children of ``.featuresBlock``
 *   -- the overlay sits *outside* the second section in the legacy template.
 *   This nesting is load-bearing: the SCSS relies on
 *   ``section:nth-of-type(1)/(2)`` flex rules and positions ``img.overlay``
 *   absolutely within the flex wrapper. The overlay also carries ``left``
 *   for left orientation; a null overlay image renders no img at all.
 */
export const FeaturesModule = (props: Props): React.JSX.Element => {
    const v = props.value;
    const ctas = v.features_cta_button ?? [];
    const features = v.features ?? [];
    const isLeft = v.orientation === "left";
    const sectionClass = v.features_image
        ? concatClassNames([styles.withImage, isLeft ? styles.left : null])
        : "";
    return (
        <div className={styles.featuresBlock}>
            <section className={sectionClass}>
                {v.features_title ? <h2>{v.features_title}</h2> : null}
                {v.features_description ? (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: v.features_description,
                        }}
                    />
                ) : null}
                {ctas.map((cta, idx) => (
                    <a key={idx} className="button" href={cta.link?.url ?? "#"}>
                        {cta.text}
                    </a>
                ))}
                {features.length > 0 && (
                    <ul>
                        {features.map((feature, idx) => (
                            <li key={idx}>
                                <WagtailOriginalImage image={feature.icon} />
                                <h6>{feature.title}</h6>
                                <p>{feature.description}</p>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
            {v.features_image && (
                <>
                    <section>
                        <WagtailOriginalImage image={v.features_image} />
                    </section>
                    <WagtailOriginalImage
                        image={v.features_overlay_image}
                        className={concatClassNames([
                            styles.overlay,
                            isLeft ? styles.left : null,
                        ])}
                    />
                </>
            )}
        </div>
    );
};
